import { Link } from "react-router-dom";
import { Check, ArrowRight, Camera, Link2, Megaphone, CalendarRange, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

const includedIcons = [Camera, Link2, Megaphone];

export default function OfferSection() {
  const { t } = useLanguage();
  const { localePath } = useLocalizedPath();
  const offer = (t as any).servicePages?.offer;

  if (!offer) return null;

  const { main, secondary, hybrid } = offer;

  return (
    <section className="py-16 md:py-24 bg-background" aria-labelledby="offer-heading">
      <div className="container">
        {/* Header */}
        <header className="text-center mb-12 max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4">
            <div className="flex flex-col items-end gap-[3px]" aria-hidden="true">
              <span className="w-12 h-px bg-gold/60" />
              <span className="w-8 h-px bg-gold/60" />
              <span className="w-5 h-px bg-gold/60" />
            </div>
            <p className="text-label text-gold">{offer.sectionLabel}</p>
            <div className="flex flex-col items-start gap-[3px]" aria-hidden="true">
              <span className="w-12 h-px bg-gold/60" />
              <span className="w-8 h-px bg-gold/60" />
              <span className="w-5 h-px bg-gold/60" />
            </div>
          </div>
          <h2 id="offer-heading" className="text-display text-3xl md:text-4xl lg:text-5xl text-foreground">
            {offer.sectionTitle}
          </h2>
          <p className="text-muted-foreground mt-4">{offer.sectionDescription}</p>
        </header>

        {/* Two offers */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 items-start max-w-6xl mx-auto">
          {/* Main offer */}
          <article className="lg:col-span-2 relative border border-gold bg-card p-6 md:p-10">
            <span className="absolute -top-3 left-6 md:left-10 bg-gold text-black text-[11px] tracking-[0.2em] uppercase px-3 py-1">
              {main.badge}
            </span>

            <h3 className="text-display text-sm sm:text-base md:text-xl lg:text-2xl text-foreground whitespace-nowrap">{main.title}</h3>
            <p className="text-gold text-sm tracking-[0.2em] uppercase mt-2">{main.subtitle}</p>

            <div className="mt-6 pb-6 border-b border-gold/20">
              <div className="flex items-baseline gap-3">
                <span className="text-display text-5xl md:text-6xl text-gold leading-none">{main.price}</span>
                <span className="text-foreground text-base md:text-lg">{main.priceUnit}</span>
              </div>
              <p className="text-foreground text-sm mt-3 font-medium">{main.priceNote}</p>
            </div>

            <p className="text-muted-foreground text-sm leading-relaxed mt-5">{main.intro}</p>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-8">
              <ul className="space-y-3">
                {(main.leftHighlights || []).map((item: string, i: number) => (
                  <li key={`l-${i}`} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-gold mt-1 flex-shrink-0" />
                    <span className="text-foreground text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <ul className="space-y-3">
                {(main.rightHighlights || []).map((item: string, i: number) => (
                  <li key={`r-${i}`} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-gold mt-1 flex-shrink-0" />
                    <span className="text-foreground text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Button asChild size="lg" className="group mt-9 w-full sm:w-auto bg-gold text-black hover:bg-gold/90">
              <Link to={localePath("/services/gestion-locative")} aria-label={`${main.cta} – ${main.title}`}>
                {main.cta}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </article>

          {/* Secondary offer */}
          <article className="border border-border bg-card/40 p-6 md:p-8 h-full flex flex-col">
            <h3 className="text-display text-2xl text-foreground">{secondary.title}</h3>
            <p className="text-muted-foreground text-xs tracking-[0.2em] uppercase mt-2">{secondary.subtitle}</p>

            <div className="flex items-baseline gap-2 mt-5 pb-5 border-b border-border">
              <span className="text-display text-3xl text-foreground leading-none">{secondary.price}</span>
              <span className="text-muted-foreground text-sm">{secondary.priceUnit}</span>
            </div>

            <p className="text-muted-foreground text-sm leading-relaxed mt-4">{secondary.intro}</p>

            <ul className="mt-5 border-t border-border divide-y divide-border">
              {(secondary.priceRows || []).map((row: { label: string; price: string }, i: number) => (
                <li key={i} className="flex items-center justify-between py-3">
                  <span className="text-foreground text-sm">{row.label}</span>
                  <span className="text-gold text-sm font-medium whitespace-nowrap ml-4">{row.price}</span>
                </li>
              ))}
            </ul>

            <ul className="space-y-2.5 mt-5">
              {(secondary.highlights || []).map((item: string, i: number) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-gold/70 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">{item}</span>
                </li>
              ))}
            </ul>

            <Button asChild variant="outline" size="sm" className="group/btn mt-auto pt-0 w-full border-gold/30 text-gold hover:bg-gold hover:text-black">
              <Link to={localePath("/services/prestations-a-la-carte")} aria-label={`${secondary.cta} – ${secondary.title}`}>
                {secondary.cta}
                <ArrowRight className="ml-1.5 w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </article>
        </div>

        {/* Included at no extra cost */}
        <div className="max-w-6xl mx-auto mt-10">
          <p className="text-label text-gold text-center mb-6 inline-flex items-center justify-center gap-2 w-full">
            <Gift className="w-4 h-4 text-gold" aria-hidden="true" />
            {offer.includedTitle}
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {(offer.included || []).map((item: { title: string; description: string }, i: number) => {
              const Icon = includedIcons[i % includedIcons.length];
              return (
                <div key={i} className="border border-border bg-card/40 p-5">
                  <Icon className="w-5 h-5 text-gold mb-3" aria-hidden="true" />
                  <h4 className="text-foreground text-base md:text-lg font-display mb-1.5">{item.title}</h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Hybrid strategy band */}
        <div className="max-w-6xl mx-auto mt-6 border border-gold/25 bg-gold/5 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <CalendarRange className="w-8 h-8 text-gold flex-shrink-0" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-label text-gold mb-1">{hybrid.label}</p>
            <h3 className="text-display text-xl md:text-2xl text-foreground mb-2">{hybrid.title}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{hybrid.description}</p>
          </div>
          <Button asChild variant="outline" size="sm" className="group/h border-gold/40 text-gold hover:bg-gold hover:text-black flex-shrink-0">
            <Link to={localePath("/services/bail-mobilite-aix-en-provence")} aria-label={`${hybrid.cta} – ${hybrid.title}`}>
              {hybrid.cta}
              <ArrowRight className="ml-1.5 w-3.5 h-3.5 group-hover/h:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
